import React, { useState, useEffect } from 'react';
import API_BASE from '../config';
import {
    FiMessageSquare,
    FiEdit3,
    FiImage,
    FiArrowRight,
    FiBell,
    FiTrendingUp,
    FiClock
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [recentMsgs, setRecentMsgs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API_BASE}/admin/dashboard_stats.php`)
            .then(r => r.json())
            .then(data => {
                if (data.status === 'success') {
                    setStats(data.stats);
                    setRecentMsgs(data.recentMessages || []);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E63E8]"></div>
        </div>
    );

    const statCards = [
        { label: 'Total Inquiries', value: stats?.messages || 0, icon: FiMessageSquare, color: 'blue', path: '/admin/messages' },
        { label: 'Published Blogs', value: stats?.blogs || 0, icon: FiEdit3, color: 'green', path: '/admin/blogs' },
        { label: 'Portfolio Projects', value: stats?.projects || 0, icon: FiImage, color: 'amber', path: '/admin/projects' },
    ];

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {statCards.map((c) => (
                    <Link
                        key={c.label}
                        to={c.path}
                        className="bg-white p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all group"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-slate-500 text-sm font-semibold uppercase tracking-wider">{c.label}</p>
                                <h3 className="text-3xl font-bold text-slate-900 mt-1">{c.value}</h3>
                            </div>
                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors
                ${c.color === 'blue' ? 'bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white' : ''}
                ${c.color === 'green' ? 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white' : ''}
                ${c.color === 'amber' ? 'bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white' : ''}
              `}>
                                <c.icon size={24} />
                            </div>
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-400">
                            <FiTrendingUp className="text-emerald-500" />
                            <span>+12% from last month</span>
                        </div>
                    </Link>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Recent Messages */}
                <div className="lg:col-span-2 bg-white rounded-2xl border shadow-sm overflow-hidden">
                    <div className="p-6 border-b flex items-center justify-between">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2">
                            <FiBell className="text-[#2E63E8]" />
                            Recent Inquiries
                        </h3>
                        <Link to="/admin/messages" className="text-sm text-slate-500 hover:text-[#2E63E8] font-semibold underline underline-offset-4">
                            View All
                        </Link>
                    </div>
                    <div className="p-0">
                        {recentMsgs.length === 0 ? (
                            <div className="p-12 text-center text-slate-400">No new messages</div>
                        ) : (
                            <div className="divide-y">
                                {recentMsgs.map((msg) => (
                                    <div key={msg.id} className="p-6 flex items-start justify-between hover:bg-slate-50 transition-colors">
                                        <div className="flex gap-4">
                                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center font-bold text-slate-500 shrink-0 capitalize">
                                                {msg.name[0]}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-900">{msg.name}</h4>
                                                <p className="text-sm text-slate-500">{msg.phone}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                <FiClock />
                                                {new Date(msg.created_at).toLocaleDateString()}
                                            </div>
                                            <button className="text-xs text-[#2E63E8] font-bold mt-2 hover:underline">Mark as Read</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Tips / Info */}
                <div className="space-y-6">
                    <div className="bg-[#1C2541] text-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
                        <div className="relative z-10">
                            <h3 className="font-bold text-xl mb-3">Admin Tip</h3>
                            <p className="text-emerald-100/70 text-sm leading-relaxed mb-6">
                                Regularly updating your blog helps improve your website's Google ranking and keeps clients engaged.
                            </p>
                            <Link to="/admin/blogs" className="btn-yellow inline-flex px-6 py-2.5 text-xs">
                                Write a Post →
                            </Link>
                        </div>
                        <div className="absolute top-[-20%] right-[-10%] w-32 h-32 bg-emerald-700/30 rounded-full blur-2xl" />
                    </div>

                    <div className="bg-white border p-6 rounded-2xl shadow-sm">
                        <h3 className="font-bold text-slate-800 mb-4">System Status</h3>
                        <div className="space-y-4">
                            {[
                                { label: 'API Connection', status: 'Healthy', color: 'bg-emerald-500' },
                                { label: 'Database Sync', status: 'Online', color: 'bg-emerald-500' },
                                { label: 'Media Storage', status: '92% Free', color: 'bg-blue-500' },
                            ].map(s => (
                                <div key={s.label} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-500">{s.label}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-slate-900 font-semibold">{s.status}</span>
                                        <div className={`w-2 h-2 rounded-full ${s.color}`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
